import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { Check, InfoIcon } from 'lucide-react'

import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'

import {
  registeredSlashWebhookUrl,
  savedSlashKey,
  slashFormSchema,
  toSlashCredentials,
  type SlashFormValues,
} from './slash-form.helpers'

type Props = {
  defaultValues?: Record<string, any>
  onSubmit: (data: Record<string, any>) => Promise<void>
  isLoading?: boolean
}

const SLASH_API_KEYS_URL = 'https://app.slash.com/global-settings/api-keys'

/** What Slash can and cannot do, said before the merchant connects it. */
const CAPABILITY_KEYS = [
  'slash.capabilityCurrencies',
  'slash.capabilityCardsOnly',
  'slash.capabilityNoSubscriptions',
  'slash.capabilityNoOneClickUpsell',
  'slash.capabilityNoTestMode',
] as const

export function SlashForm({ defaultValues = {}, onSubmit, isLoading = false }: Props) {
  const { t } = useTranslation('integrations')
  const savedKey = savedSlashKey(defaultValues.credentials)
  const webhookUrl = registeredSlashWebhookUrl(defaultValues.metadata)

  const form = useForm<SlashFormValues>({
    resolver: zodResolver(slashFormSchema(savedKey !== null)),
    defaultValues: {
      apiKey: '',
      legalEntityId: defaultValues.credentials?.legalEntityId ?? '',
    },
  })

  const handleFormSubmit = async (values: SlashFormValues) => {
    await onSubmit({ credentials: toSlashCredentials(values) })
  }

  const apiKeyPlaceholder = savedKey
    ? t('slash.apiKeyReplacePlaceholder', { key: savedKey })
    : t('slash.apiKeyPlaceholder')

  return (
    <div className="space-y-6">
      {savedKey && (
        <Alert className="border-green-200 bg-green-50">
          <Check className="h-4 w-4 text-green-600" />
          <AlertTitle className="text-green-900">{t('slash.connectedTitle')}</AlertTitle>
          <AlertDescription className="text-green-700">{t('slash.connectedDescription', { key: savedKey })}</AlertDescription>
        </Alert>
      )}

      <form id="integration-config-form" onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>{t('slash.credentialsTitle')}</CardTitle>
            <CardDescription>{t('slash.credentialsDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="slash-api-key">
                {t('slash.apiKey')} {!savedKey && <span className="text-destructive">*</span>}
              </Label>
              <Input
                id="slash-api-key"
                type="password"
                autoComplete="off"
                placeholder={apiKeyPlaceholder}
                disabled={isLoading}
                {...form.register('apiKey')}
              />
              <p className="text-xs text-muted-foreground">{t('slash.apiKeyHint')}</p>
              {form.formState.errors.apiKey && (
                <p className="text-xs text-destructive">{t('slash.apiKeyRequired')}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="slash-legal-entity">{t('slash.legalEntityId')}</Label>
              <Input
                id="slash-legal-entity"
                placeholder={t('slash.legalEntityIdPlaceholder')}
                disabled={isLoading}
                {...form.register('legalEntityId')}
              />
              <p className="text-xs text-muted-foreground">{t('slash.legalEntityIdHint')}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('slash.webhookTitle')}</CardTitle>
            <CardDescription>{t('slash.webhookDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            {webhookUrl ? (
              <div className="space-y-2">
                <p className="flex items-center gap-2 text-sm text-green-700">
                  <Check className="h-4 w-4" />
                  {t('slash.webhookRegistered')}
                </p>
                <code className="block rounded bg-muted px-3 py-2 text-xs break-all">{webhookUrl}</code>
              </div>
            ) : (
              <p className="flex items-center gap-2 text-sm text-muted-foreground">
                <InfoIcon className="h-4 w-4" />
                {t('slash.webhookPending')}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('slash.capabilitiesTitle')}</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground">
              {CAPABILITY_KEYS.map((key) => (
                <li key={key}>{t(key)}</li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('slash.setupTitle')}</CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
              <li>
                {t('slash.setupStep1')}{' '}
                <a href={SLASH_API_KEYS_URL} target="_blank" rel="noopener noreferrer" className="text-primary underline">
                  {t('slash.setupStep1Link')}
                </a>
              </li>
              <li>{t('slash.setupStep2')}</li>
              <li>{t('slash.setupStep3')}</li>
            </ol>
          </CardContent>
        </Card>
      </form>
    </div>
  )
}
